// ── Panel de plataforma — lista de cuentas (2026-08-17) ──────────────────────
// List Pattern, variante multi-columna (DESIGN_SYSTEM.md § "Multi-column list
// (table variant)"): grid CSS con los MISMOS tokens que ListItem, no un
// DataTable ni una tabla suelta. SelectionActionBar a page root, post-FilterRow,
// full-width (REGLA #7) — el error de ReportsPage fue montarla dentro del panel.

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AccountStatus } from '@evo-hub/shared';
import { Checkbox } from '../components/ui/Checkbox.js';
import { StripeTestModeBanner } from '../components/ui/StripeTestModeBanner.js';
import { useStripeMode } from '../hooks/useStripeMode.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { ListState } from '../components/ui/ListState.js';
import { Pill } from '../components/PillGroup.js';
import { useToast } from '../components/ui/Toast.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { parseServerDate } from '../lib/date.js';
import { ApiError } from '../lib/api.js';
import {
  usePlatformAccounts, useChangeAccountStatus,
  type PlatformAccountRow,
} from '../hooks/usePlatformAccounts.js';

// Columna de identidad ~2fr, el resto reparte parejo — misma regla que
// LeadsPage. El primer tramo replica el ancho de la celda del checkbox para
// que la lista arranque alineada con las de los demás módulos.
const COL_TEMPLATE =
  'var(--evo-table-check-col)'
  + ' minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1fr)';

const HEADER_CELL: React.CSSProperties = {
  padding: 'var(--evo-table-head-py) var(--evo-table-cell-px)',
  color: 'var(--evo-fg-3)',
  fontSize: 'var(--evo-list-header-fs)',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
};

const CELL: React.CSSProperties = {
  padding: 'var(--evo-table-cell-py) var(--evo-table-cell-px)',
  fontSize: 'var(--evo-list-title-fs)',
  color: 'var(--evo-fg-2)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const STATUS_TONE: Record<AccountStatus, 'success' | 'amber' | 'danger' | 'muted'> = {
  active: 'success',
  provisioning: 'muted',
  suspended: 'amber',
  closed: 'danger',
};

const SUBSCRIPTION_TONE: Record<string, 'success' | 'amber' | 'danger'> = {
  active: 'success',
  grace_period: 'amber',
  suspended: 'danger',
};

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  return parseServerDate(iso).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Stripe manda los montos en centavos. */
function fmtMoney(amountCents: number | null, currency: string | null): string | null {
  if (amountCents == null) return null;
  return new Intl.NumberFormat('es', { style: 'currency', currency: (currency ?? 'usd').toUpperCase() })
    .format(amountCents / 100);
}

function fmtUsd(value: number | null): string | null {
  if (value == null) return null;
  return new Intl.NumberFormat('es', { style: 'currency', currency: 'USD' }).format(value);
}

export function PlatformAccountsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setOverride } = useTopBarCtx();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error, refetch } = usePlatformAccounts();
  const changeStatus = useChangeAccountStatus();
  const accounts = useMemo(() => data?.accounts ?? [], [data]);

  useEffect(() => {
    setOverride({
      title: t('platformAdmin.accountsTitle'),
      subtitle: t('platformAdmin.accountsSubtitle'),
      // REGLA #7: variante -large (viewBox 0 0 56 56). La de sidebar (18×18)
      // renderizaría el stroke 3× más grueso.
      heroIcon: 'accounts',
      heroIconColor: 'var(--evo-cyan)',
    });
    return () => setOverride({});
  }, [t, setOverride]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter(a => {
      if (statusFilter && a.status !== statusFilter) return false;
      if (!q) return true;
      return a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q);
    });
  }, [accounts, search, statusFilter]);

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));

  async function applyStatus(status: Extract<AccountStatus, 'suspended' | 'active'>) {
    const ids = [...selected];
    const results = await Promise.allSettled(
      ids.map(id => changeStatus.mutateAsync({ id, status })),
    );
    const failed = results.filter(r => r.status === 'rejected');
    const okCount = results.length - failed.length;

    if (okCount > 0) {
      toast.success(t(status === 'suspended' ? 'platformAdmin.suspended' : 'platformAdmin.reactivated', { count: okCount }));
    }
    // Un fallo parcial se dice, no se esconde detrás del éxito de los demás.
    if (failed.length > 0) {
      const first = failed[0] as PromiseRejectedResult;
      toast.error(first.reason instanceof ApiError ? first.reason.message : String(first.reason));
    }
    setSelected(new Set());
  }

  const { data: stripeMode } = useStripeMode();

  return (
    <div className="flex flex-col h-full">
      {/* Esta pantalla muestra último pago y próximo cobro de cada cuenta. Si
          nada de eso es dinero real, hay que verlo antes que los números. */}
      <StripeTestModeBanner visible={stripeMode?.isTest} />

      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder={t('platformAdmin.searchPlaceholder')}
        />
        <FilterRow.Dropdown<string>
          value={statusFilter}
          placeholder={t('platformAdmin.statusFilter.all')}
          options={[
            { value: '', label: t('platformAdmin.statusFilter.all') },
            { value: 'active', label: t('platformAdmin.statusFilter.active') },
            { value: 'suspended', label: t('platformAdmin.statusFilter.suspended') },
            // Suspendidas y cerradas van separadas a propósito: son decisiones
            // distintas (una es "no pagó", la otra es "se terminó"), y juntarlas
            // en un "inactivas" borraría justamente lo que hay que distinguir.
            { value: 'closed', label: t('platformAdmin.statusFilter.closed') },
            { value: 'provisioning', label: t('platformAdmin.statusFilter.provisioning') },
          ]}
          onChange={setStatusFilter}
        />
      </FilterRow>

      {/* REGLA #7: page root, inmediatamente después del FilterRow, full-width. */}
      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('platformAdmin.selected', { count: selected.size })}
        onClearSelection={() => setSelected(new Set())}
      >
        <SelectionActionBar.Action
          label={t('platformAdmin.bulk.reactivate')}
          tone="primary"
          loading={changeStatus.isPending}
          onClick={() => { void applyStatus('active'); }}
          confirmRequired
        />
        <SelectionActionBar.Divider />
        <SelectionActionBar.Action
          label={t('platformAdmin.bulk.suspend')}
          tone="amber"
          loading={changeStatus.isPending}
          onClick={() => { void applyStatus('suspended'); }}
          confirmRequired
        />
        {/* Cerrar NO está acá: es de a una cuenta y pide tipear el slug. Un
            "cerrar 4 cuentas" con un solo clic es exactamente el accidente que
            la confirmación por id existe para prevenir. */}
      </SelectionActionBar>

      <div style={{
        display: 'grid', gridTemplateColumns: COL_TEMPLATE,
        height: 'var(--evo-toolbar-height)', alignItems: 'center',
        borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0,
        background: 'var(--evo-bg-2)',
      }}>
        <div style={{ padding: 'var(--evo-table-head-py) var(--evo-list-checkbox-gap) var(--evo-table-head-py) var(--evo-list-pad-x)' }}>
          <Checkbox
            checked={allSelected}
            indeterminate={selected.size > 0 && !allSelected}
            onChange={() => {
              setSelected(allSelected ? new Set() : new Set(rows.map(r => r.id)));
            }}
          />
        </div>
        <div style={{ ...HEADER_CELL, paddingLeft: 0 }}>{t('platformAdmin.columns.account')}</div>
        <div style={HEADER_CELL}>{t('platformAdmin.columns.subscription')}</div>
        <div style={HEADER_CELL}>{t('platformAdmin.columns.clientSince')}</div>
        <div style={HEADER_CELL}>{t('platformAdmin.columns.lastPayment')}</div>
        <div style={HEADER_CELL}>{t('platformAdmin.columns.nextCharge')}</div>
        <div style={{ ...HEADER_CELL, paddingRight: 'var(--evo-list-pad-x)', textAlign: 'right' }}>{t('platformAdmin.columns.monthSpend')}</div>
      </div>

      <div className="flex-1 overflow-auto">
        {/* Precedencia: error ANTES que vacío. Un 429 se veía como "no hay
            cuentas" — el peor mensaje posible, porque hace pensar que se
            perdieron los datos. Ver components/ui/ListState.tsx. */}
        <ListState
          isLoading={isLoading}
          isError={isError}
          error={error}
          isEmpty={rows.length === 0}
          emptyTitle={t('platformAdmin.accountsEmpty')}
          onRetry={() => { void refetch(); }}
        >
        {rows.map((account, i) => (
          <div key={account.id}>
            <AccountRow
              account={account}
              selected={selected.has(account.id)}
              onToggleSelect={() => toggleSelect(account.id)}
              onOpen={() => navigate(`/admin/cuentas/${account.id}`)}
            />
            {/* Divisor con margen lateral, nunca full-bleed (List Pattern). */}
            {i < rows.length - 1 && (
              <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)', flexShrink: 0 }} />
            )}
          </div>
        ))}
        </ListState>
      </div>
    </div>
  );
}

interface AccountRowProps {
  account: PlatformAccountRow;
  selected: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}

function AccountRow({ account, selected, onToggleSelect, onOpen }: AccountRowProps) {
  const { t } = useTranslation();
  const dash = t('platformAdmin.noData');

  const subscriptionLabel = account.billingExempt
    ? t('platformAdmin.subscription.exempt')
    : t(`platformAdmin.subscription.${account.subscriptionStatus}`);

  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: COL_TEMPLATE,
        minHeight: 'var(--evo-list-row-min-height)', alignItems: 'stretch',
        cursor: 'pointer',
      }}
      onClick={onOpen}
    >
      <div
        style={{ padding: 'calc(var(--evo-table-cell-py) + var(--evo-list-check-nudge)) var(--evo-list-checkbox-gap) var(--evo-table-cell-py) var(--evo-list-pad-x)', alignSelf: 'flex-start' }}
        onClick={e => e.stopPropagation()}
      >
        <Checkbox checked={selected} onChange={onToggleSelect} />
      </div>

      <div style={{ ...CELL, paddingLeft: 0, whiteSpace: 'normal' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--evo-fg-1)', fontWeight: 500 }}>{account.name}</span>
          <Pill size="xs" tone={STATUS_TONE[account.status]}>
            {t(`platformAdmin.status.${account.status}`)}
          </Pill>
        </div>
        <div style={{ fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', marginTop: '2px' }}>
          {account.id}
        </div>
      </div>

      <div style={CELL}>
        <Pill size="xs" tone={account.billingExempt ? 'muted' : SUBSCRIPTION_TONE[account.subscriptionStatus] ?? 'muted'}>
          {subscriptionLabel}
        </Pill>
      </div>

      <div style={CELL}>{fmtDate(account.createdAt) ?? dash}</div>

      <div style={CELL}>
        {account.lastPaymentAt
          ? `${fmtMoney(account.lastPaymentAmount, account.lastPaymentCurrency) ?? ''} · ${fmtDate(account.lastPaymentAt)}`.trim()
          : dash}
      </div>

      <div style={CELL}>{fmtDate(account.nextChargeAt) ?? dash}</div>

      {/* Se muestra lo COBRABLE, no el bruto: es el número que iría a una
          factura. El bruto queda debajo en gris cuando el cupo gratuito cubrió
          algo — si no se viera la diferencia, la columna estaría diciendo que
          el cliente debe plata que EvoHub nunca pagó.
          null significa "no se pudo leer", no "gastó cero" — por eso cae al
          guión y nunca a $0.00. */}
      <div style={{ ...CELL, paddingRight: 'var(--evo-list-pad-x)', textAlign: 'right', whiteSpace: 'normal' }}>
        {fmtUsd(account.monthBillableUsd ?? account.monthSpendUsd) ?? dash}
        {account.monthLimitUsd != null && (
          <span style={{ color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-meta-fs)' }}>
            {' / '}{fmtUsd(account.monthLimitUsd)}
          </span>
        )}
        {account.monthCoveredUsd != null && account.monthCoveredUsd > 0 && (
          <div style={{ color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-meta-fs)' }}>
            {t('platformAdmin.grossWithCredit', {
              gross: fmtUsd(account.monthSpendUsd),
              covered: fmtUsd(account.monthCoveredUsd),
            })}
          </div>
        )}
      </div>
    </div>
  );
}
